import type { TemplateComponent, TemplateButton } from './whatsapp-business-sdk/types';

export interface TemplateParameter {
  name: string;
  component: 'HEADER' | 'BODY' | 'BUTTONS';
  index: number;
  label: string;
  inputType: 'text' | 'image' | 'video' | 'document' | 'url' | 'copy_code' | 'payload';
}

export interface TemplateParameterValues {
  [paramName: string]: string;
}

const PLACEHOLDER_REGEX = /\{\{(\d+)\}\}/g;
const NAMED_PLACEHOLDER_REGEX = /\{\{([a-zA-Z_][\w.-]*)\}\}/g;
const NUMBERED_PARAM_PATTERN = /^(body|header)_\d+$/;

function isNamedParam(param: TemplateParameter): boolean {
  return !NUMBERED_PARAM_PATTERN.test(param.name);
}

function getInputTypeForFormat(format?: string): TemplateParameter['inputType'] {
  switch (format) {
    case 'IMAGE': return 'image';
    case 'VIDEO': return 'video';
    case 'DOCUMENT': return 'document';
    default: return 'text';
  }
}

export function getTemplateParameters(components: TemplateComponent[]): TemplateParameter[] {
  const params: TemplateParameter[] = [];

  for (const comp of components) {
    if (comp.type === 'HEADER' && comp.text) {
      const inputType = getInputTypeForFormat(comp.format);
      let match: RegExpExecArray | null;
      // Try numbered params first ({{1}}, {{2}})
      PLACEHOLDER_REGEX.lastIndex = 0;
      let foundNumbered = false;
      while ((match = PLACEHOLDER_REGEX.exec(comp.text)) !== null) {
        foundNumbered = true;
        const idx = parseInt(match[1], 10);
        params.push({
          name: `header_${idx}`,
          component: 'HEADER',
          index: idx,
          label: `Header variable ${idx}`,
          inputType,
        });
      }
      // If no numbered params, try named params ({{name}})
      if (!foundNumbered) {
        let namedIdx = 1;
        NAMED_PLACEHOLDER_REGEX.lastIndex = 0;
        while ((match = NAMED_PLACEHOLDER_REGEX.exec(comp.text)) !== null) {
          params.push({
            name: match[1],
            component: 'HEADER',
            index: namedIdx++,
            label: match[1],
            inputType,
          });
        }
      }
    }

    if (comp.type === 'BODY' && comp.text) {
      let match: RegExpExecArray | null;
      // Try numbered params first ({{1}}, {{2}})
      PLACEHOLDER_REGEX.lastIndex = 0;
      let foundNumbered = false;
      while ((match = PLACEHOLDER_REGEX.exec(comp.text)) !== null) {
        foundNumbered = true;
        const idx = parseInt(match[1], 10);
        params.push({
          name: `body_${idx}`,
          component: 'BODY',
          index: idx,
          label: `Body variable ${idx}`,
          inputType: 'text',
        });
      }
      // If no numbered params, try named params ({{name}})
      if (!foundNumbered) {
        let namedIdx = 1;
        NAMED_PLACEHOLDER_REGEX.lastIndex = 0;
        while ((match = NAMED_PLACEHOLDER_REGEX.exec(comp.text)) !== null) {
          params.push({
            name: match[1],
            component: 'BODY',
            index: namedIdx++,
            label: match[1],
            inputType: 'text',
          });
        }
      }
    }

    if (comp.type === 'BUTTONS' && comp.buttons) {
      for (let i = 0; i < comp.buttons.length; i++) {
        const btn = comp.buttons[i];
        if (btn.type === 'URL' && btn.url) {
          PLACEHOLDER_REGEX.lastIndex = 0;
          NAMED_PLACEHOLDER_REGEX.lastIndex = 0;
          if (PLACEHOLDER_REGEX.test(btn.url) || NAMED_PLACEHOLDER_REGEX.test(btn.url)) {
            params.push({
              name: `button_${i}_url`,
              component: 'BUTTONS',
              index: i,
              label: `Button "${btn.text}" URL suffix`,
              inputType: 'url',
            });
          }
        }
        if (btn.type === 'COPY_CODE') {
          params.push({
            name: `button_${i}_copy_code`,
            component: 'BUTTONS',
            index: i,
            label: `Button "${btn.text}" coupon code`,
            inputType: 'copy_code',
          });
        }
        if (btn.type === 'QUICK_REPLY') {
          params.push({
            name: `button_${i}_payload`,
            component: 'BUTTONS',
            index: i,
            label: `Button "${btn.text}" payload`,
            inputType: 'payload',
          });
        }
      }
    }
  }

  return params;
}

export function formatForWhatsAppAPI(
  params: TemplateParameter[],
  values: TemplateParameterValues
): object[] {
  const componentMap: Record<string, { type: string; parameters: object[] }> = {};

  const INPUT_TO_SUB_TYPE: Record<string, string> = {
    url: 'url',
    copy_code: 'copy_code',
    payload: 'quick_reply',
  };

  for (const param of params) {
    if (param.component === 'BUTTONS') {
      const key = `button_${param.index}`;
      if (!(key in componentMap)) {
        componentMap[key] = { type: 'button', sub_type: INPUT_TO_SUB_TYPE[param.inputType] || 'url', parameters: [] };
      }
      if (param.inputType === 'copy_code') {
        componentMap[key].parameters.push({
          type: 'coupon_code',
          coupon_code: values[param.name] ?? '',
        });
      } else if (param.inputType === 'payload') {
        componentMap[key].parameters.push({
          type: 'payload',
          payload: values[param.name] ?? '',
        });
      } else {
        componentMap[key].parameters.push({
          type: 'text',
          text: values[param.name] ?? '',
        });
      }
    } else {
      const compType = param.component.toLowerCase();
      if (!(compType in componentMap)) {
        componentMap[compType] = { type: compType, parameters: [] };
      }
      const paramType = param.inputType === 'text' || param.inputType === 'url'
        ? 'text'
        : param.inputType;

      if (paramType === 'text') {
        const paramObj: Record<string, string> = {
          type: 'text',
          text: values[param.name] ?? '',
        };
        if (isNamedParam(param)) {
          paramObj.parameter_name = param.name;
        }
        componentMap[compType].parameters.push(paramObj);
      } else {
        const paramObj: Record<string, unknown> = {
          type: paramType,
          [paramType]: { link: values[param.name] ?? '' },
        };
        if (isNamedParam(param)) {
          paramObj.parameter_name = param.name;
        }
        componentMap[compType].parameters.push(paramObj);
      }
    }
  }

  const result: object[] = [];
  if (componentMap['header']) result.push(componentMap['header']);
  if (componentMap['body']) result.push(componentMap['body']);

  for (const key of Object.keys(componentMap).sort()) {
    if (key.startsWith('button_')) {
      result.push({ ...componentMap[key], index: parseInt(key.split('_')[1], 10) });
    }
  }

  return result;
}

export function formatForStorage(
  params: TemplateParameter[],
  values: TemplateParameterValues
): object {
  const mappings: Record<string, { value: string; component: string; index: number; inputType: string }> = {};
  for (const param of params) {
    mappings[param.name] = {
      value: values[param.name] ?? '',
      component: param.component,
      index: param.index,
      inputType: param.inputType,
    };
  }
  return mappings;
}

export function getBodyText(components: TemplateComponent[]): string | undefined {
  const body = components.find((c) => c.type === 'BODY');
  return body?.text;
}

export function getHeaderInfo(
  components: TemplateComponent[]
): { type?: string; text?: string; format?: string } | undefined {
  const header = components.find((c) => c.type === 'HEADER');
  if (!header) return undefined;
  return { type: header.type, text: header.text, format: header.format };
}

export function getButtons(components: TemplateComponent[]): TemplateButton[] {
  const buttons = components.find((c) => c.type === 'BUTTONS');
  return buttons?.buttons ?? [];
}

export function countParameters(components: TemplateComponent[]): number {
  return getTemplateParameters(components).length;
}
